import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/**
 * AdminActionMenu - Hiérarchise les actions selon design system:
 * - primaryAction: violet, visible
 * - secondaryActions: gris, ghost buttons
 * - dangerousActions: rouge, UNIQUEMENT dans modale
 */
export default function AdminActionMenu({
  primaryAction,
  secondaryActions = [],
  dangerousActions = []
}) {
  const [confirmDialog, setConfirmDialog] = useState(null);

  const handleDangerousClick = (action) => {
    setConfirmDialog(action);
  };

  const confirmAction = () => {
    if (confirmDialog?.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog(null);
  };

  return (
    <>
      {/* Confirmation Dialog for Dangerous Actions */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="bg-[#13151D] border-[#6E2BFF] text-white">
          <DialogTitle className="text-white">⚠️ {confirmDialog?.label}</DialogTitle>
          <DialogDescription className="text-[#B3B6C2]">
            {confirmDialog?.message || 'Cette action est irréversible. Êtes-vous sûr?'}
          </DialogDescription>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={() => setConfirmDialog(null)}
              variant="outline"
              className="border-[#6E2BFF] text-[#6E2BFF]"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmAction}
              disabled={confirmDialog?.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {confirmDialog?.isPending ? 'En cours...' : 'Confirmer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex gap-2 items-center w-full">
         {/* Primary Action - Violet, visible */}
         {primaryAction ? (
           <Button
             onClick={primaryAction.onClick}
             disabled={primaryAction.disabled}
             className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-semibold flex-1"
             size={primaryAction.size || 'sm'}
           >
             {primaryAction.icon && <primaryAction.icon className="w-4 h-4 mr-1" />}
             {primaryAction.label}
           </Button>
         ) : (
           <div className="flex-1" />
         )}

        {/* Secondary Actions - Gris, ghost */}
        {secondaryActions.filter(Boolean).map((action, idx) => (
          <Button
            key={idx}
            onClick={action.onClick}
            disabled={action.disabled}
            variant="ghost"
            className="text-[#B3B6C2] hover:text-white hover:bg-[#1B1E28] flex-shrink-0"
            size={action.size || 'sm'}
          >
            {action.icon && <action.icon className="w-4 h-4 mr-1" />}
            {action.label}
          </Button>
        ))}

        {/* Dangerous Actions - Menu with confirmation */}
        {dangerousActions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-[#B3B6C2] hover:text-white hover:bg-[#1B1E28]"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#13151D] border-[#6E2BFF]">
              {dangerousActions.map((action, idx) => (
                <DropdownMenuItem
                  key={idx}
                  onClick={() => handleDangerousClick(action)}
                  className="text-red-400 hover:bg-red-600/10 cursor-pointer"
                >
                  {action.icon && <action.icon className="w-4 h-4 mr-2" />}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </>
  );
}